import { useEffect, useRef } from 'react';
import {
    FinancingApi,
    LoanStatus,
    GetLoanApiResponse,
    SubmitApplicationDecision,
} from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reportSecondLookAttempt } from '@servicetitan/financing-utils-shared';

const DEFAULT_POLL_INTERVAL = 5000; // 5 seconds
const DEFAULT_MAX_POLL_TIME = 120000; // 2 minutes

/** Statuses that end polling. Anything else is still in progress, however it maps to a decision. */
const isTerminalStatus = (status?: LoanStatus) =>
    status === LoanStatus.Declined ||
    status === LoanStatus.Rejected ||
    status === LoanStatus.Approved ||
    status === LoanStatus.Prequalified ||
    status === LoanStatus.Error;

/**
 * A non-null redirectUrl is its own terminal outcome. Some providers return a redirect while status
 * is still Review (non-terminal), so without this the poller would spin indefinitely.
 */
const isTerminalResponse = (loanData?: GetLoanApiResponse) =>
    isTerminalStatus(loanData?.status) || !!loanData?.redirectUrl;

export interface UseLoanStatusPollingOptions {
    /** Loan ID to poll for status updates */
    loanId: string | null;
    /** Custom poll interval in milliseconds (default: 5000) */
    pollInterval?: number;
    /** Custom max poll time in milliseconds (default: 120000) */
    maxPollTime?: number;
    /** Custom timeout callback */
    onTimeout?: (loanId: string) => void;
    /** Custom decision change callback - called when loan decision changes */
    onDecisionChange?: (
        decision: SubmitApplicationDecision,
        loanData: GetLoanApiResponse,
        loanId: string,
    ) => void;
    /** Whether polling is enabled (default: true when loanId exists) */
    enabled?: boolean;
}

export interface UseLoanStatusPollingReturn {
    /** Current loan data from polling */
    loanData: GetLoanApiResponse | undefined;
    /** Whether polling is currently active */
    isPolling: boolean;
    /** Stop polling manually */
    stopPolling: () => void;
    /** Start polling manually */
    startPolling: () => void;
    /** Reset polling state */
    reset: () => void;
}

export const useLoanStatusPolling = (
    options: UseLoanStatusPollingOptions,
): UseLoanStatusPollingReturn => {
    const {
        loanId,
        pollInterval = DEFAULT_POLL_INTERVAL,
        maxPollTime = DEFAULT_MAX_POLL_TIME,
        onTimeout,
        onDecisionChange,
        enabled = true,
    } = options;

    const [financingApi] = useDependencies(FinancingApi);
    const queryClient = useQueryClient();
    const timeoutRef = useRef<number>();
    const lastStatusRef = useRef<LoanStatus>();

    // Set up timeout when polling starts
    useEffect(() => {
        if (loanId && enabled) {
            timeoutRef.current = setTimeout(() => {
                // Cancel the polling query
                queryClient.cancelQueries({ queryKey: ['loanStatus', loanId] });

                // A lender still reviewing at the ceiling is a different outcome from one that
                // never said anything, and the UI cannot tell them apart — both land on the same
                // decline screen. No-ops unless this is a second-look loan.
                const status = lastStatusRef.current;
                reportSecondLookAttempt(
                    loanId,
                    status === LoanStatus.Review || status === LoanStatus.AboutToSubmit
                        ? 'review'
                        : 'timeout',
                );

                if (onTimeout) {
                    onTimeout(loanId);
                }
                // Note: Decision data and workflow stage management should be handled in the component
            }, maxPollTime);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [loanId, enabled, queryClient, onTimeout, maxPollTime]);

    // Query for polling loan status
    const { data: loanData, refetch } = useQuery<GetLoanApiResponse>({
        queryKey: ['loanStatus', loanId],
        queryFn: async () => {
            const response = await financingApi.loansGetUpdatedApplication(loanId!);
            return response.data;
        },
        enabled: !!loanId && enabled,
        refetchInterval: query => (isTerminalResponse(query.state.data) ? false : pollInterval),
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Effect to handle loan status changes
    useEffect(() => {
        if (loanData?.status && loanId) {
            lastStatusRef.current = loanData.status;
            const isTerminal = isTerminalResponse(loanData);

            if (timeoutRef.current && isTerminal) {
                clearTimeout(timeoutRef.current);
            }

            let decision: SubmitApplicationDecision;

            // redirectUrl is a distinct terminal outcome — checked before status mapping so a
            // Review + redirectUrl loan does not keep producing Review (which leaves the UI stuck).
            if (loanData.redirectUrl) {
                decision = SubmitApplicationDecision.Submitted;
            } else {
                // Map loan status to decision
                switch (loanData.status) {
                    case LoanStatus.Declined:
                    case LoanStatus.Rejected:
                        decision = SubmitApplicationDecision.Declined;
                        break;
                    case LoanStatus.Approved:
                    case LoanStatus.Prequalified:
                        decision = SubmitApplicationDecision.Approved;
                        break;
                    case LoanStatus.Review:
                    case LoanStatus.AboutToSubmit:
                        decision = SubmitApplicationDecision.Review;
                        break;
                    default:
                        decision = SubmitApplicationDecision.Error;
                        break;
                }
            }

            // Only a terminal status ends the customer's wait. The switch above maps every
            // in-progress status to Error via its default arm, so keying off the decision instead
            // would stop the measurement on the first poll. No-ops unless this is a second-look
            // loan, and only the first terminal poll reports.
            if (isTerminal) {
                reportSecondLookAttempt(
                    loanId,
                    loanData.redirectUrl
                        ? 'redirect'
                        : decision === SubmitApplicationDecision.Approved
                          ? 'approved'
                          : decision === SubmitApplicationDecision.Declined
                            ? 'declined'
                            : 'error',
                );
            }

            // Always call the callback if provided
            if (onDecisionChange) {
                onDecisionChange(decision, loanData, loanId);
            }
        }
    }, [loanData, loanId, onDecisionChange]);

    // Polling can be torn down before any decision arrives: a status the consumer does not
    // recognise sends the primary/manual path back to the application stage, which unmounts this
    // hook and the ceiling timer with it. Nothing else would resolve the attempt, so the journey
    // would vanish from the outcome mix rather than count as a failure. Keyed on loanId alone so
    // an unstable callback cannot retrigger it, and a no-op once a poll or the ceiling reported.
    useEffect(() => {
        if (!loanId) {
            return;
        }

        return () => {
            reportSecondLookAttempt(loanId, 'error');
        };
    }, [loanId]);

    const stopPolling = () => {
        queryClient.cancelQueries({ queryKey: ['loanStatus', loanId] });
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const startPolling = () => {
        if (loanId) {
            refetch();
        }
    };

    const reset = () => {
        stopPolling();
    };

    return {
        loanData,
        isPolling: !!loanId && enabled,
        stopPolling,
        startPolling,
        reset,
    };
};
